import { useEffect, useRef } from 'react';

const MIN_DX = 60;
const MAX_MS = 700;
// a touch this close to the screen edge belongs to the system back/forward gesture
const EDGE = 24;

/** something under the finger scrolls sideways itself (a row of chips): the swipe is its own */
function scrollsSideways(target: Element) {
  for (let n: Element | null = target; n && n !== document.body; n = n.parentElement) {
    if (n.scrollWidth > n.clientWidth + 1 && /(auto|scroll)/.test(getComputedStyle(n).overflowX)) return true;
  }
  return false;
}

/**
 * A quick horizontal swipe anywhere on the screen while the calling page is shown (phones): the empty
 * space under a short list counts too. Vertical scrolling, open sheets, fields, the tab bar and
 * sideways-scrolling rows are left alone; the swipe has to be clearly more horizontal than vertical.
 */
export function useSwipe(onSwipe: (dir: 'left' | 'right') => void) {
  const latest = useRef(onSwipe);
  latest.current = onSwipe;
  useEffect(() => {
    let s: { x: number; y: number; at: number } | null = null;
    const start = (e: TouchEvent) => {
      s = null;
      const p = e.touches[0];
      const target = e.target as Element;
      if (e.touches.length !== 1 || !p || p.clientX < EDGE || p.clientX > window.innerWidth - EDGE) return;
      if (document.querySelector('dialog[open]') || target.closest('input, textarea, select, .tabbar, .sidebar') || scrollsSideways(target)) return;
      s = { x: p.clientX, y: p.clientY, at: performance.now() };
    };
    const end = (e: TouchEvent) => {
      const p = e.changedTouches[0];
      if (!s || !p) return;
      const dx = p.clientX - s.x, dy = p.clientY - s.y, quick = performance.now() - s.at < MAX_MS;
      s = null;
      if (quick && Math.abs(dx) >= MIN_DX && Math.abs(dx) > 2 * Math.abs(dy)) latest.current(dx < 0 ? 'left' : 'right');
    };
    const cancel = () => { s = null; };
    document.addEventListener('touchstart', start, { passive: true });
    document.addEventListener('touchend', end, { passive: true });
    document.addEventListener('touchcancel', cancel, { passive: true });
    return () => {
      document.removeEventListener('touchstart', start);
      document.removeEventListener('touchend', end);
      document.removeEventListener('touchcancel', cancel);
    };
  }, []);
}
